import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import SkeletonLoader from "@/components/ui/SkeletonLoader";
import TableTitleBar from "@/charts/TableTitleBar";
import { getSkeletonAlignmentStyle } from "@/charts/tableUtils";

const NColumnTable = ({
  data,
  n,
  columnKeys = [],
  columnTitles = [],
  columnRatio = "",
  columnAlignment = "",
  title,
  isLoaded = false,
  error = null,
  boxHeight = "400px",
  minCellHeight = "2.5rem",
  itemsPerPage = 5,
  pagination = false,
  wrapLength = null,
  previewColumn = null,
  wrapText = false,
  className = "",
  exportData,
  noDataMessage,
  ...rest
}) => {
  const { t } = useTranslation();
  const resolvedNoDataMessage =
    noDataMessage ?? t("common.noDataGeneric", "No data available");
  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredRow, setHoveredRow] = useState(null);

  // Validation
  const validateProps = () => {
    const errors = [];

    if (n < 2 || n > 10) {
      errors.push("n must be between 2 and 10");
    }

    if (columnKeys.length !== n) {
      errors.push(
        `columnKeys length (${columnKeys.length}) must equal n (${n})`,
      );
    }

    if (columnTitles.length !== n) {
      errors.push(
        `columnTitles length (${columnTitles.length}) must equal n (${n})`,
      );
    }

    const ratioArray = columnRatio.split("-").filter((r) => r);
    if (ratioArray.length !== n) {
      errors.push(`columnRatio must have ${n} values separated by dashes`);
    }

    const alignArray = columnAlignment.split("-").filter((a) => a);
    if (alignArray.length !== n) {
      errors.push(`columnAlignment must have ${n} values separated by dashes`);
    }

    return errors;
  };

  const validationErrors = validateProps();

  if (validationErrors.length > 0) {
    console.error("Validation Errors:", validationErrors);
  }

  // Parse column ratio to get percentage widths
  const columnWidths = columnRatio.split("-").map(Number);

  // Parse column alignment
  const columnAlignments = columnAlignment.split("-");

  const getAlignmentClass = (align) => {
    switch (align) {
      case "l":
        return "text-start";
      case "r":
        return "text-end";
      case "c":
        return "text-center";
      default:
        return "text-start";
    }
  };

  // Calculate pagination values
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = pagination ? data.slice(startIndex, endIndex) : data;

  // Check if headers should be shown
  const showHeaders = columnTitles.some((title) => title);

  // Function to truncate text if wrapLength is specified
  const truncateText = (text, maxLength) => {
    if (!maxLength || !text || typeof text !== "string") return text;
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  // Helper function to determine preview position
  const getPreviewPosition = (rowIndex, totalRows) => {
    // For tables with 2 or fewer rows, always show below.
    // For tables with more than 2 rows, show above for the last 2 rows, otherwise show below.
    if (totalRows <= 2) {
      return "below";
    }
    const isNearBottom = rowIndex >= totalRows - 2;
    return isNearBottom ? "above" : "below";
  };

  // Helper function to render cell content
  const renderCellContent = (content, alignment, shouldTruncate = true) => {
    if (React.isValidElement(content)) {
      return <div className={getAlignmentClass(alignment)}>{content}</div>;
    }

    if (typeof content === "string") {
      return (
        <div
          className={`${
            shouldTruncate && !wrapLength && !wrapText ? "text-truncate" : ""
          } ${getAlignmentClass(alignment)}`}
          dangerouslySetInnerHTML={{
            __html: wrapLength ? truncateText(content, wrapLength) : content,
          }}
        />
      );
    }

    if (Array.isArray(content)) {
      return (
        <div
          className={`${
            shouldTruncate ? "text-truncate" : ""
          } ${getAlignmentClass(alignment)}`}
        >
          {content.join(", ")}
        </div>
      );
    }

    return (
      <div
        className={`${
          shouldTruncate && !wrapText ? "text-truncate" : ""
        } ${getAlignmentClass(alignment)}`}
      >
        {content != null ? String(content) : ""}
      </div>
    );
  };

  // Navigation handlers
  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const exportColumns = columnKeys.map((key, i) => ({
    key,
    title: columnTitles[i] || key,
  }));

  // Handle loading state
  if (!isLoaded) {
    const skeletonRows = Array.from({ length: Math.max(4, itemsPerPage) });
    return (
      <div
        className={`bg-white border card-radius${className ? ` ${className}` : ""}`}
        style={{
          borderColor: "var(--border)",
          boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
        {...rest}
      >
        {title && (
          <div className="px-3 py-2 border-bottom bg-light">
            <div className="text-sm fw-medium text-dark">{title}</div>
          </div>
        )}
        {showHeaders && (
          <div className="px-3 py-2 border-bottom bg-light">
            <div className="row g-0">
              {columnTitles.map((_, idx) => (
                <div
                  key={`skeleton-header-${idx}`}
                  style={{ width: `${columnWidths[idx]}%` }}
                >
                  <div className={getAlignmentClass(columnAlignments[idx])}>
                    <SkeletonLoader
                      width="58%"
                      height="12px"
                      style={getSkeletonAlignmentStyle(columnAlignments[idx])}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{ height: boxHeight, overflowY: "hidden" }}>
          {skeletonRows.map((_, rowIdx) => (
            <div
              key={`skeleton-row-${rowIdx}`}
              className="px-3 py-2 border-bottom"
              style={{ minHeight: minCellHeight }}
            >
              <div className="row g-0 align-items-center">
                {columnKeys.map((_, colIdx) => (
                  <div
                    key={`skeleton-cell-${rowIdx}-${colIdx}`}
                    style={{ width: `${columnWidths[colIdx]}%` }}
                  >
                    <div
                      className={getAlignmentClass(columnAlignments[colIdx])}
                    >
                      <SkeletonLoader
                        width={
                          colIdx === 0
                            ? rowIdx % 2 === 0
                              ? "84%"
                              : "70%"
                            : colIdx === columnKeys.length - 1
                              ? "46%"
                              : "58%"
                        }
                        height="14px"
                        style={getSkeletonAlignmentStyle(
                          columnAlignments[colIdx],
                        )}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Handle no data state
  if (isLoaded && data.length === 0) {
    return (
      <div
        className={`bg-white border card-radius${className ? ` ${className}` : ""}`}
        style={{
          borderColor: "var(--border)",
          boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
        {...rest}
      >
        {title && (
          <div className="px-3 py-2 border-bottom bg-light">
            <div className="text-sm fw-medium text-dark">{title}</div>
          </div>
        )}
        <div
          className="d-flex justify-content-center text-muted"
          style={{ height: boxHeight, paddingTop: "1rem" }}
        >
          <div>
            <i className="bi bi-table me-2"></i>
            {resolvedNoDataMessage}
          </div>
        </div>
      </div>
    );
  }

  // Handle validation errors
  if (isLoaded && validationErrors.length > 0) {
    return (
      <div
        className={`bg-white border card-radius${className ? ` ${className}` : ""}`}
        style={{
          borderColor: "var(--border)",
          boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
        {...rest}
      >
        {title && (
          <div className="px-3 py-2 border-bottom bg-light">
            <div className="text-sm fw-medium text-dark">{title}</div>
          </div>
        )}
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: boxHeight }}
        >
          <div className="w-100 text-center align-self-start m-3">
            <div className="alert alert-danger mb-0" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {t(
                "common.loadError",
                "An error occurred while loading the data. Please try again later.",
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle error state
  if (isLoaded && error) {
    return (
      <div
        className={`bg-white border card-radius${className ? ` ${className}` : ""}`}
        style={{
          borderColor: "var(--border)",
          boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
        {...rest}
      >
        {title && (
          <div className="px-3 py-2 border-bottom bg-light">
            <div className="text-sm fw-medium text-dark">{title}</div>
          </div>
        )}
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: boxHeight }}
        >
          <div className="w-100 text-center align-self-start m-3">
            <div className="alert alert-danger mb-0" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {t(
                "common.loadError",
                "An error occurred while loading the data. Please try again later.",
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle loaded data state
  return (
    <div
      className={`bg-white border card-radius${className ? ` ${className}` : ""}`}
      style={{
        borderColor: "var(--border)",
        boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}
      {...rest}
    >
      {(title || exportData) && (
        <TableTitleBar
          title={title}
          data={data}
          columns={exportColumns}
          exportData={exportData}
        />
      )}

      {/* Table Header */}
      {showHeaders && (
        <div className="px-3 py-2 border-bottom bg-light">
          <div className="row g-0">
            {columnTitles.map((colTitle, idx) => (
              <div key={idx} style={{ width: `${columnWidths[idx]}%` }}>
                <div
                  className={`text-sm fw-medium text-dark ${getAlignmentClass(
                    columnAlignments[idx],
                  )}`}
                >
                  {colTitle || ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table Body */}
      <div
        style={{
          height: pagination ? "auto" : boxHeight,
          overflowY: pagination ? "visible" : "auto",
        }}
      >
        {/* Render actual data rows */}
        {currentData.map((item, index) => {
          const rowId = pagination ? startIndex + index : index;
          const hasPreview = previewColumn && item[previewColumn];
          const previewPosition = getPreviewPosition(index, currentData.length);

          return (
            <div
              key={item.id || `item-${rowId}`}
              className="px-3 py-2 text-sm hoverable-row border-bottom"
              style={{ minHeight: minCellHeight, position: "relative" }}
            >
              <div className="row g-0 align-items-center">
                {columnKeys.map((key, idx) => (
                  <div
                    key={idx}
                    style={{ width: `${columnWidths[idx]}%` }}
                    onMouseEnter={() =>
                      idx === 0 && hasPreview && setHoveredRow(rowId)
                    }
                    onMouseLeave={() => idx === 0 && setHoveredRow(null)}
                  >
                    {renderCellContent(
                      item[key],
                      columnAlignments[idx],
                      idx !== 0 || !wrapLength,
                    )}
                  </div>
                ))}
              </div>

              {/* Preview Box */}
              {hasPreview && hoveredRow === rowId && (
                <div
                  style={{
                    position: "absolute",
                    top: previewPosition === "below" ? "100%" : "auto",
                    bottom: previewPosition === "above" ? "100%" : "auto",
                    left: "0",
                    zIndex: 1000,
                    backgroundColor: "var(--bg-elevated)",
                    border: "1px solid var(--border)",
                    borderRadius: "4px",
                    padding: "12px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    minWidth: "200px",
                    maxWidth: "400px",
                    marginTop: previewPosition === "below" ? "4px" : "0",
                    marginBottom: previewPosition === "above" ? "4px" : "0",
                  }}
                >
                  {item[previewColumn]}
                </div>
              )}
            </div>
          );
        })}

        {/* Render empty placeholder rows (only for pagination) */}
        {pagination &&
          Array.from(
            { length: itemsPerPage - currentData.length },
            (_, index) => (
              <div
                key={`empty-${index}`}
                className="px-3 py-2 text-sm"
                style={{ minHeight: minCellHeight }}
              >
                <div className="row g-0">
                  {columnKeys.map((_, idx) => (
                    <div key={idx} style={{ width: `${columnWidths[idx]}%` }}>
                      <div
                        className={`text-truncate ${getAlignmentClass(
                          columnAlignments[idx],
                        )}`}
                      >
                        &nbsp;
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ),
          )}
      </div>

      {/* Table Footer with Pagination */}
      {pagination && (
        <div
          className="px-3 py-2 border-top"
          style={{ backgroundColor: "var(--bg-surface)" }}
        >
          <style>{`
            .ncol-page-btn {
              border: 1px solid var(--border);
              background: var(--bg-elevated);
              color: var(--text-secondary);
              border-radius: 6px;
              transition: background 0.15s, border-color 0.15s, color 0.15s;
            }
            .ncol-page-btn:hover:not(:disabled) {
              background: var(--color-bg-hover-1);
              border-color: var(--brand-color-1);
              color: var(--brand-color-1);
            }
            .ncol-page-btn:disabled {
              opacity: 0.45;
              cursor: not-allowed;
            }
          `}</style>
          <div className="d-flex justify-content-center align-items-center">
            <button
              className="btn btn-sm ncol-page-btn me-3"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              style={{ minWidth: "30px" }}
            >
              &lt;
            </button>

            <span className="text-sm text-muted">
              {t("common.pageOf", {
                current: currentPage,
                total: totalPages,
                defaultValue: `Page ${currentPage} of ${totalPages}`,
              })}
            </span>

            <button
              className="btn btn-sm ncol-page-btn ms-3"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              style={{ minWidth: "30px" }}
            >
              &gt;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NColumnTable;
